import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leaderboardApi } from '../api/client';
import { LeaderboardEntry } from '../types';
import { useAuthStore } from '../store/authStore';

type TabType = 'global' | 'beginner' | 'easy' | 'medium' | 'hard';

const tabs: { id: TabType; label: string; emoji: string }[] = [
  { id: 'global', label: '总榜', emoji: '🌍' },
  { id: 'beginner', label: '初学者', emoji: '🌸' },
  { id: 'easy', label: '简单', emoji: '🌱' },
  { id: 'medium', label: '中等', emoji: '⚡' },
  { id: 'hard', label: '困难', emoji: '🔥' },
];

export default function LeaderboardPage() {
  const { user, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('global');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [myRanking, setMyRanking] = useState<{
    globalRank: number | null;
    totalScore: number;
    gamesCompleted: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (activeTab === 'global') {
          const response = await leaderboardApi.getGlobal(50);
          setLeaderboard(response.leaderboard);
        } else {
          const response = await leaderboardApi.getByDifficulty(activeTab, 50);
          setLeaderboard(response.leaderboard);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '加载排行榜失败';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaderboard();
  }, [activeTab]);

  useEffect(() => {
    const fetchMyRanking = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await leaderboardApi.getMyRanking();
        setMyRanking(response);
      } catch (err) {
        console.error('Failed to fetch my ranking:', err);
      }
    };

    fetchMyRanking();
  }, [isAuthenticated]);

  const getRankStyle = (rank: number): string => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500 to-amber-500';
      case 2:
        return 'bg-gradient-to-r from-gray-400 to-gray-500';
      case 3:
        return 'bg-gradient-to-r from-orange-600 to-amber-700';
      default:
        return 'bg-white/10';
    }
  };

  const getRankEmoji = (rank: number): string => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return '';
    }
  };

  return (
    <div className="py-4 sm:py-8">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">🏆 排行榜</h1>
        <p className="text-sm sm:text-base text-gray-500">与其他玩家一较高下</p>
      </div>

      {/* My Ranking Card */}
      {isAuthenticated && myRanking && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 sm:p-6 mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.username}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-lg sm:text-xl font-semibold text-white">
                  {user?.username?.[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-semibold text-sm sm:text-base">{user?.username}</p>
                <p className="text-xs sm:text-sm text-gray-500">我的排名</p>
              </div>
            </div>
            <div className="flex justify-around sm:justify-end gap-4 sm:gap-8 text-center">
              <div>
                <p className="text-xl sm:text-2xl font-bold text-blue-500">
                  {myRanking.globalRank || '-'}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">全球排名</p>
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-yellow-500">
                  {myRanking.totalScore}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">总分</p>
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-green-500">
                  {myRanking.gamesCompleted}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">完成关卡</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 px-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all text-sm sm:text-base whitespace-nowrap touch-manipulation ${
              activeTab === tab.id
                ? 'bg-green-500 text-white'
                : 'bg-white/80 hover:bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <span className="mr-1 sm:mr-2">{tab.emoji}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin w-10 h-10 sm:w-12 sm:h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Leaderboard Table */}
      {!isLoading && !error && (
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card overflow-hidden overflow-x-auto"
        >
          {leaderboard.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              暂无排行数据
            </div>
          ) : (
            <table className="w-full min-w-[320px]">
              <thead className="border-b border-gray-200">
                <tr className="text-left text-gray-500 text-xs sm:text-sm">
                  <th className="px-3 sm:px-6 py-3 sm:py-4 w-16 sm:w-20">排名</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4">玩家</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-right">总分</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-right hidden sm:table-cell">完成关卡</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, index) => (
                  <motion.tr
                    key={entry.userId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`border-b border-gray-100 ${
                      user?.id === entry.userId ? 'bg-green-50' : ''
                    }`}
                  >
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-semibold ${getRankStyle(
                          entry.rank
                        )}`}
                      >
                        {getRankEmoji(entry.rank) || entry.rank}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {entry.avatar ? (
                          <img
                            src={entry.avatar}
                            alt={entry.username}
                            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-xs sm:text-sm font-semibold text-white flex-shrink-0">
                            {entry.username?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-sm sm:text-base truncate max-w-[100px] sm:max-w-none">{entry.username}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold text-yellow-500 text-sm sm:text-base">
                      {entry.totalScore || entry.score}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-right text-gray-500 hidden sm:table-cell">
                      {entry.gamesCompleted || '-'}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      )}
    </div>
  );
}

